import React, { useEffect, useState } from 'react';
import { convertToDateDefault } from 'js/library/utils/helpers';
import { useHistory } from 'react-router';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import { useParams } from 'react-router-dom';

// STYLES
import {
  CircularProgress,
  Pagination,
  Table,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableBody,
  IconButton,
  MenuItem,
  Button,
} from '@mui/material';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import { DotsThreeVertical, PencilSimple, Trash } from 'phosphor-react';

// COMPONENTS
import { ButtonsHeaderContent } from './ButtonsHeaderContent';
import { DeleteAlertModal } from './DeleteAlertModal';
import { MenuIconButton, StyledMenu } from '../../Campaigns/ListCampaigns/styles';
import { PageStructure } from 'js/components/TriiboComponents/PageStructure/PageStructure';
import usePagination from 'js/components/TriiboComponents/Tables/usePagination';

// APIS
import { getAlerts } from 'js/library/utils/API/apiGetAlerts';
import { getUserSegment } from 'js/library/utils/API/Segmentation/getUserSegment';
import { updateAlerts } from 'js/library/utils/API/updateAlerts';
import { toast } from 'react-toastify';

export function AlertManagement() {
  const history = useHistory();
  const { id } = useParams();
  const partnerId = localStorage.getItem('partnerIdClube');

  const [alertSelectedToBeDelete, setAlertSelectedToBeDelete] = useState('');
  const [alertList, setAlertList] = useState([]);
  const [filterType, setFilterType] = useState('popup');
  const [loading, setLoading] = useState(true);
  const [loadingOrder, setLoadingOrder] = useState(false);
  const [menus, setMenus] = useState({});
  const [openModalDelete, setOpenModalDelete] = useState(false);
  const [priorities, setPriorities] = useState([]);
  const [segments, setSegments] = useState([]);
  const [status, setStatus] = useState(true);
  const [reload, setReload] = useState(false);
  const [updatedAlerts, setUpdatedAlerts] = useState([]);

  const PER_PAGE = 10;
  const _DATA = usePagination(alertList, PER_PAGE);
  const count = Math.ceil(alertList.length / PER_PAGE);
  const isOrderChanged = JSON.stringify(alertList) !== JSON.stringify(updatedAlerts);

  useEffect(() => {
    const fetchSegments = async () => {
      try {
        const data = { from: 0, size: 100, partnerId };
        const result = await getUserSegment(data);
        setSegments(result.map((segment) => ({ name: segment.name, id: segment.id })));
      } catch (error) {
        console.error('Erro ao buscar segmentos:', error);
      }
    };
    fetchSegments();
  }, [partnerId]);

  useEffect(() => {
    const fetchAlerts = async () => {
      try {
        const result = await getAlerts(partnerId);
        const transformedArray = result
          ? Object.keys(result).map((key) => ({ alertId: key, ...result[key] }))
          : [];

        const now = Date.now();
        transformedArray.sort((a, b) => a.priority - b.priority);
        setAlertList(
          transformedArray.filter(
            (alert) =>
              alert.type === filterType && (status ? +alert.endDate > now : +alert.endDate <= now)
          )
        );

        // PEGA AS PRIORIDADES E O TIPO PARA ENVIAR PRO CREATE PARA SABER QUAL PRIORITY SETAR
        const listPriorities = [];
        transformedArray.map((alert) => {
          if (now < +alert.endDate) {
            listPriorities.push({
              type: alert.type,
              priority: alert.priority,
            });
          }
        });

        setPriorities(listPriorities);
      } catch (error) {
        console.error(error);
      } finally {
        setTimeout(() => setLoading(false), 2000);
      }
    };
    fetchAlerts();
  }, [filterType, partnerId, reload, status]);

  const handleChangePage = (e, p) => {
    _DATA.jump(p);
  };

  const onDragEnd = (result) => {
    if (!result.destination) return;

    const reorderedAlerts = Array.from(alertList);
    const [movedAlert] = reorderedAlerts.splice(result.source.index, 1);
    reorderedAlerts.splice(result.destination.index, 0, movedAlert);

    const updatedAlerts = reorderedAlerts.map((alert, index) => ({
      ...alert,
      priority: index + 1,
    }));

    setAlertList(updatedAlerts);
    setUpdatedAlerts(updatedAlerts);
  };

  const redirectEditAlert = (event, selected) => {
    event.preventDefault();

    history.push({
      pathname: `/admin/seu-clube/gestao-alertas/${id}/editar`,
      state: {
        alertSettings: selected,
      },
    });
  };

  const handleClick = (event, alertId) => {
    setMenus((prevMenus) => ({
      ...prevMenus,
      [alertId]: event.currentTarget,
    }));
  };

  const handleClose = (alertId) => {
    setMenus((prevMenus) => ({
      ...prevMenus,
      [alertId]: null,
    }));
  };

  const handleModalDelete = (event, alertId) => {
    event.preventDefault();

    if (!openModalDelete) {
      setAlertSelectedToBeDelete(alertId);
    }
    setOpenModalDelete(!openModalDelete);
  };

  const sanitizeNameAction = (action) => {
    switch (action) {
      case 'validEmail':
        return 'Validação de email';
      case 'fillingSocialNetwork':
        return 'Preenchimento de redes sociais';
    }
  };

  const updateOrder = async () => {
    setLoadingOrder(true);
    try {
      await Promise.all(
        updatedAlerts.map(async (alert) => {
          const updatedPayload = { ...alert, priority: alert.priority ?? 0 };
          delete updatedPayload.updateDate;

          await updateAlerts(updatedPayload);
        })
      );
      toast.success('Prioridades atualizadas com sucesso!');
    } catch (error) {
      toast.error('Erro ao atualizar prioridades:', error);
    } finally {
      setLoadingOrder(false);
    }
  };

  return (
    <PageStructure title="Gestão de alertas">
      <ButtonsHeaderContent
        filterType={filterType}
        setFilterType={setFilterType}
        status={status}
        setStatus={setStatus}
        priorities={priorities}
      />

      {loading ? (
        <CircularProgress size={50} thickness={5} />
      ) : status ? (
        <DragDropContext onDragEnd={onDragEnd}>
          <Droppable droppableId="alerts">
            {(provided) => (
              <TableContainer {...provided.droppableProps} ref={provided.innerRef}>
                <Table sx={{ minWidth: 650 }} size="medium">
                  <TableHead>
                    <TableRow>
                      <TableCell align="left"></TableCell>
                      <TableCell align="left">
                        <strong>Status</strong>
                      </TableCell>
                      <TableCell align="left">
                        <strong>Tipo</strong>
                      </TableCell>
                      <TableCell align="left">
                        <strong>Segmentos</strong>
                      </TableCell>
                      <TableCell align="left">
                        <strong>Ação</strong>
                      </TableCell>
                      <TableCell align="left">
                        <strong>Data do início</strong>
                      </TableCell>
                      <TableCell align="left">
                        <strong>Data do fim</strong>
                      </TableCell>
                      <TableCell align="left">
                        <strong>Ações</strong>
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {_DATA.currentData().map((alert, index) => (
                      <Draggable key={alert.alertId} draggableId={alert.alertId} index={index}>
                        {(provided) => (
                          <TableRow ref={provided.innerRef} {...provided.draggableProps}>
                            <TableCell align="left" {...provided.dragHandleProps}>
                              <IconButton>
                                <DragIndicatorIcon sx={{ cursor: 'grab' }} />
                              </IconButton>
                            </TableCell>
                            <TableCell
                              align="left"
                              sx={{
                                color: +alert.startDate > Date.now() ? 'orange' : 'green',
                                fontWeight: 700,
                              }}
                            >
                              {+alert.startDate > Date.now() ? 'Ativo/Em espera' : 'Ativo'}
                            </TableCell>
                            <TableCell align="left">{alert.type}</TableCell>
                            <TableCell align="left">
                              {!alert.segments
                                ? 'Todos os segmentos'
                                : alert.segments
                                    .map(
                                      (segmentId) =>
                                        segments.find((segment) => segment.id === segmentId)
                                          ?.name || 'Todos os segmentos'
                                    )
                                    .join(', ')}
                            </TableCell>
                            <TableCell align="left">{sanitizeNameAction(alert.action)}</TableCell>
                            <TableCell align="left">
                              {convertToDateDefault(+alert.startDate)}
                            </TableCell>
                            <TableCell align="left">
                              {convertToDateDefault(+alert.endDate)}
                            </TableCell>
                            <TableCell align="left">
                              <IconButton
                                id={`menu-button-${alert.alertId}`}
                                aria-controls={
                                  menus[alert.alertId] ? `menu-${alert.alertId}` : undefined
                                }
                                aria-haspopup="true"
                                onClick={(event) => handleClick(event, alert.alertId)}
                                sx={{
                                  backgroundColor: '#ebe6ea',
                                  borderRadius: '50px',
                                  color: 'gray',
                                  '&:hover': { color: '#6e3296' },
                                }}
                              >
                                <DotsThreeVertical size={28} weight="bold" />
                              </IconButton>
                              <StyledMenu
                                id={`menu-${alert.alertId}`}
                                anchorEl={menus[alert.alertId]}
                                open={Boolean(menus[alert.alertId])}
                                onClose={() => handleClose(alert.alertId)}
                                MenuListProps={{
                                  'aria-labelledby': `menu-button-${alert.alertId}`,
                                }}
                              >
                                <MenuItem onClick={handleClose} disableRipple>
                                  <MenuIconButton
                                    to="#"
                                    onClick={(event) => redirectEditAlert(event, alert)}
                                  >
                                    <PencilSimple size={20} weight="bold" />
                                    Editar
                                  </MenuIconButton>
                                </MenuItem>
                                <MenuItem onClick={handleClose} disableRipple>
                                  <MenuIconButton
                                    to="#"
                                    onClick={(event) => handleModalDelete(event, alert.alertId)}
                                  >
                                    <Trash size={20} weight="bold" />
                                    Deletar
                                  </MenuIconButton>
                                </MenuItem>
                              </StyledMenu>
                            </TableCell>
                          </TableRow>
                        )}
                      </Draggable>
                    ))}
                    {provided.placeholder}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Droppable>
          {alertList.length === 0 ? null : (
            <Button
              color="primary"
              disabled={isOrderChanged || loadingOrder}
              onClick={updateOrder}
              sx={{ minWidth: '10rem' }}
              variant="contained"
            >
              {loadingOrder ? (
                <CircularProgress size={25} thickness={6} sx={{ color: '#fff' }} />
              ) : (
                'Atualizar prioridade'
              )}
            </Button>
          )}
        </DragDropContext>
      ) : (
        <>
          <TableContainer>
            <Table sx={{ minWidth: 650 }} size="medium">
              <TableHead>
                <TableRow>
                  <TableCell align="left">
                    <strong>Status</strong>
                  </TableCell>
                  <TableCell align="left">
                    <strong>Tipo</strong>
                  </TableCell>
                  <TableCell align="left">
                    <strong>Segmentos</strong>
                  </TableCell>
                  <TableCell align="left">
                    <strong>Ação</strong>
                  </TableCell>
                  <TableCell align="left">
                    <strong>Data do início</strong>
                  </TableCell>
                  <TableCell align="left">
                    <strong>Data do fim</strong>
                  </TableCell>
                  <TableCell align="left">
                    <strong>Ações</strong>
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {_DATA.currentData().map((alert) => (
                  <TableRow key={alert.alertId}>
                    <TableCell align="left" sx={{ color: 'red', fontWeight: 700 }}>
                      Expirado
                    </TableCell>
                    <TableCell align="left">{alert.type}</TableCell>
                    <TableCell align="left">
                      {!alert.segments
                        ? 'Todos os segmentos'
                        : alert.segments
                            .map(
                              (segmentId) =>
                                segments.find((segment) => segment.id === segmentId)?.name ||
                                'Todos os segmentos'
                            )
                            .join(', ')}
                    </TableCell>
                    <TableCell align="left">{sanitizeNameAction(alert.action)}</TableCell>
                    <TableCell align="left">{convertToDateDefault(+alert.startDate)}</TableCell>
                    <TableCell align="left">{convertToDateDefault(+alert.endDate)}</TableCell>
                    <TableCell align="left">
                      <IconButton
                        id={`menu-button-${alert.alertId}`}
                        aria-controls={menus[alert.alertId] ? `menu-${alert.alertId}` : undefined}
                        aria-haspopup="true"
                        onClick={(event) => handleClick(event, alert.alertId)}
                        sx={{
                          backgroundColor: '#ebe6ea',
                          borderRadius: '50px',
                          color: 'gray',
                          '&:hover': { color: '#6e3296' },
                        }}
                      >
                        <DotsThreeVertical size={28} weight="bold" />
                      </IconButton>
                      <StyledMenu
                        id={`menu-${alert.alertId}`}
                        anchorEl={menus[alert.alertId]}
                        open={Boolean(menus[alert.alertId])}
                        onClose={() => handleClose(alert.alertId)}
                        MenuListProps={{
                          'aria-labelledby': `menu-button-${alert.alertId}`,
                        }}
                      >
                        <MenuItem onClick={handleClose} disableRipple>
                          <MenuIconButton
                            to="#"
                            onClick={(event) => redirectEditAlert(event, alert)}
                          >
                            <PencilSimple size={20} weight="bold" />
                            Editar
                          </MenuIconButton>
                        </MenuItem>
                        <MenuItem onClick={handleClose} disableRipple>
                          <MenuIconButton
                            to="#"
                            onClick={(event) => handleModalDelete(event, alert.alertId)}
                          >
                            <Trash size={20} weight="bold" />
                            Deletar
                          </MenuIconButton>
                        </MenuItem>
                      </StyledMenu>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <Pagination count={count} onChange={handleChangePage} />
        </>
      )}
      <DeleteAlertModal
        alertSelectedToBeDelete={alertSelectedToBeDelete}
        open={openModalDelete}
        handleClose={handleModalDelete}
        setAlertSelectedToBeDelete={setAlertSelectedToBeDelete}
        setReload={setReload}
      />
    </PageStructure>
  );
}
